import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Suspense, lazy } from 'react'
import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { Toaster } from '@/components/ui/toaster'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ENABLE_DEVTOOLS } from '@/lib/constants'
import { PortalRedirect } from '@/portals/_shared/PortalRedirect'
import { UtilityLayout } from '@/portals/_shared/UtilityLayout'

// Portals
const ConsulentePoral = lazy(() => import('@/portals/consulente/v2'))
const PMIPortal = lazy(() => import('@/portals/pmi'))
const RicercatorePortal = lazy(() => import('@/portals/ricercatore'))
const AssociazionePortal = lazy(() => import('@/portals/associazione'))
const EntePortal = lazy(() => import('@/portals/ente_pubblico'))
const MediaPortal = lazy(() => import('@/portals/media'))
const OsservatorioPortal = lazy(() => import('@/portals/osservatorio'))
const AdminPortal = lazy(() => import('@/portals/admin'))

// Public
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const PricingPage = lazy(() => import('@/pages/Pricing').then((m) => ({ default: m.PricingPage })))
const GarantiaPage = lazy(() =>
  import('@/pages/GarantiaPage').then((m) => ({ default: m.GarantiaPage }))
)
const OnboardingPage = lazy(() =>
  import('@/pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage }))
)

// Utility (standalone — not wrapped in module layout)
const CalendarPage = lazy(() =>
  import('@/pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage }))
)
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage }))
)
const HelpCenterPage = lazy(() =>
  import('@/pages/help/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage }))
)

// Loading component for Suspense
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-primary/30 rounded-full"></div>
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-muted-foreground text-sm">Caricamento...</p>
      </div>
    </div>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/garanzia" element={<GarantiaPage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />

                {/* ═══ Portal routes ═══ */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <PortalRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/consulente/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ConsulentePoral />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pmi/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <PMIPortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ricerca/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <RicercatorePortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/associazione/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AssociazionePortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ente/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <EntePortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/media/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <MediaPortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/osservatorio/*"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <OsservatorioPortal />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />

                {/* ═══ Admin Portal ═══ */}
                <Route path="/admin/*" element={<AdminPortal />} />

                {/* ═══ Utility routes — inside portal shell (header + sidebar) ═══ */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <UtilityLayout>
                        <Suspense fallback={<PageLoader />}>
                          <ProfilePage />
                        </Suspense>
                      </UtilityLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <UtilityLayout>
                        <Suspense fallback={<PageLoader />}>
                          <CalendarPage />
                        </Suspense>
                      </UtilityLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute>
                      <UtilityLayout>
                        <Suspense fallback={<PageLoader />}>
                          <HelpCenterPage />
                        </Suspense>
                      </UtilityLayout>
                    </ProtectedRoute>
                  }
                />

                {/* ═══ Legacy module routes → redirect to portal root ═══ */}
                <Route path="/sagitta/*" element={<Navigate to="/" replace />} />
                <Route path="/cassiopeia/*" element={<Navigate to="/" replace />} />
                <Route path="/andromeda/*" element={<Navigate to="/" replace />} />
                <Route path="/cepheus/*" element={<Navigate to="/" replace />} />
                <Route path="/pegasus/*" element={<Navigate to="/" replace />} />
                <Route path="/roadmap" element={<Navigate to="/" replace />} />
                <Route path="/opportunities" element={<Navigate to="/" replace />} />
                <Route path="/partners" element={<Navigate to="/" replace />} />
                <Route path="/projects" element={<Navigate to="/" replace />} />
                <Route path="/insights" element={<Navigate to="/" replace />} />
                <Route path="/alerts" element={<Navigate to="/" replace />} />
                <Route path="/workspaces/*" element={<Navigate to="/" replace />} />
                <Route path="/observatory" element={<Navigate to="/" replace />} />
                <Route path="/proposals/*" element={<Navigate to="/" replace />} />
                <Route path="/draco/*" element={<Navigate to="/" replace />} />
                <Route path="/lyra/*" element={<Navigate to="/admin/teams" replace />} />
                <Route path="/orion/*" element={<Navigate to="/admin/api" replace />} />
                <Route path="/integration" element={<Navigate to="/" replace />} />
                <Route path="/reports" element={<Navigate to="/admin/reports" replace />} />
                <Route path="/reports/*" element={<Navigate to="/admin/reports" replace />} />
                <Route path="/search" element={<Navigate to="/" replace />} />
                <Route path="/activity" element={<Navigate to="/admin/activity" replace />} />
                <Route path="/debug" element={<Navigate to="/admin/debug" replace />} />
                <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
                <Route path="/billing" element={<Navigate to="/admin/billing" replace />} />
                <Route
                  path="/notifications"
                  element={<Navigate to="/admin/notifications" replace />}
                />
                <Route
                  path="/teams/invite"
                  element={<Navigate to="/admin/teams/invite" replace />}
                />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>

            <Toaster />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>

      {ENABLE_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default App
